// apps/web/src/features/users/components/UserDeactivateConfirm.tsx
// Spec §14.4 / plan Task 5.5.1. ReasonModal wrapping useDeactivateUser.mutate.
//
// Architecture note: `<ReasonModal>` (the 1:1 legacy port) calls its
// `onClose` handler immediately after its internal `onConfirm(value)` —
// the legacy assumption is that the caller of the modal is ready to
// unmount as soon as the button is pressed. For a mutation whose
// callbacks (onSuccess/onError → toast) we NEED to fire even after the
// dialog closes, we must keep the hook's host component mounted past the
// modal's close event. We do that by routing `onClose` through a local
// `visible` flag: the modal hides immediately (legacy UX), but this
// component stays mounted until the mutation settles. Per plan §5.5.1:
//   - cancel (Esc / backdrop / cancel):      external onClose fires.
//   - confirm → mutation success:            external onClose fires.
//   - confirm → mutation error:              dialog stays open for retry;
//                                            external onClose NOT called.
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDeactivateUser } from '@magimp/shared-hooks';
import type { User } from '@magimp/shared';
import { ReasonModal } from '../../../ui/index.js';
import { useToastStore } from '../../../stores/toast.js';
import { errorMessage } from '../../../api/errors.js';

export interface UserDeactivateConfirmProps {
  user: User;
  onClose: () => void;
}

export function UserDeactivateConfirm({ user, onClose }: UserDeactivateConfirmProps) {
  const { t } = useTranslation(['users', 'common']);
  const deactivate = useDeactivateUser(user.id);
  const show = useToastStore((s) => s.show);
  const [visible, setVisible] = useState(true);
  // Tracks whether the close was triggered by the Confirm button (which
  // also starts a mutation) vs Cancel/Esc/backdrop (no mutation). The
  // legacy ReasonModal fires onClose synchronously after onConfirm, so we
  // mark this ref inside the onConfirm handler before the close event is
  // dispatched.
  const confirmingRef = useRef(false);

  // Reconcile the hidden-modal state once the mutation settles.
  //
  // Order matters: we flip `visible` back to `true` BEFORE calling
  // `deactivate.reset()`. TanStack Query's reset propagates through its
  // own external store (useSyncExternalStore), which does not batch with
  // React's setState. If we reset first, the intermediate render sees
  // `isError=false`, `confirming=false` (the ref was cleared
  // synchronously), `visible=false` — and the effect falls through to
  // the cancel branch and erroneously calls onClose. Flipping visible
  // first makes the next render short-circuit at the `if (visible)`
  // guard regardless of the query-store render ordering.
  useEffect(() => {
    if (visible) return;
    if (confirmingRef.current) {
      // Confirm path — wait for the mutation to finish before deciding.
      if (deactivate.isPending) return;
      if (deactivate.isError) {
        // Reopen step 1 so the user can retry. Do NOT call
        // `deactivate.reset()` here — its state update propagates through
        // TanStack Query's external store and does not batch with
        // React's setState, which caused an intermediate render with
        // `visible=false, isError=false, confirming=false` that fell
        // through to the cancel branch and wrongly called onClose. The
        // next `mutate()` call transitions the state cleanly (pending →
        // success/error), so an explicit reset is unnecessary.
        confirmingRef.current = false;
        setVisible(true);
        return;
      }
      // Success (or idle — the callback never ran — tear down anyway).
      onClose();
      return;
    }
    // Cancel path — no mutation was started.
    onClose();
  }, [visible, deactivate.isPending, deactivate.isError, onClose]);

  if (!visible) return null;

  return (
    <ReasonModal
      title={t('users:modals.deactivateTitle', { name: user.name })}
      text={t('users:modals.deactivateText')}
      placeholder={t('users:placeholders.deactivationReason')}
      confirmLabel={t('common:actions.deactivate')}
      danger
      onConfirm={(reason) => {
        confirmingRef.current = true;
        deactivate.mutate(
          { reason },
          {
            onSuccess: () => {
              show({ tone: 'success', text: t('users:toasts.deactivated') });
            },
            onError: (err) => {
              show({ tone: 'error', text: errorMessage(err) });
            },
          },
        );
      }}
      onClose={() => setVisible(false)}
    />
  );
}
